import React, { useState } from 'react';

const sections = [
  {
    title: 'General Questions',
    faqs: [
      {
        q: 'What is Smile Africa?',
        a: 'Smile Africa is a digital airtime top-up platform that enables diaspora communities in the USA to buy airtime for loved ones abroad. Customers pay directly using a debit or credit card such as Visa or Mastercard.',
      },
      {
        q: 'What can I use Smile Africa for right now?',
        a: 'You can use Smile Africa to buy airtime for loved ones in Ethiopia, Kenya, Ghana and Nigeria. Pay directly with any debit or credit card.\n\nMore countries and mobile operators are coming soon.',
      },
      {
        q: 'Which countries is Smile Africa starting with?',
        a: 'Smile Africa is starting with diaspora communities in the USA buying airtime for loved ones in Ethiopia, Kenya, Ghana and Nigeria. More countries will be added soon.',
      },
      {
        q: 'Can users outside the USA use Smile Africa?',
        a: 'Smile Africa currently supports card payments from users in the USA sending airtime to Ethiopia, Kenya, Ghana and Nigeria. Support for more sending countries is planned.',
      },
    ],
  },
  {
    title: 'Airtime Purchases',
    faqs: [
      {
        q: 'How do I send airtime?',
        a: 'Open the Smile Africa app, select the recipient’s country, choose their mobile network, enter their phone number, and select an airtime amount.\n\nPay securely with your debit or credit card. The airtime is delivered directly to the recipient’s phone, usually instantly or within a few minutes.',
      },
      {
        q: 'What if the recipient does not receive the airtime?',
        a: "If your payment was completed but the recipient did not receive the airtime, go to the 'History' tab in the app and retrieve the transaction ID. Then contact Smile Africa customer support with the transaction ID so the team can review and assist you.",
      },
      {
        q: 'Can I cancel an airtime purchase after sending it?',
        a: "Once an airtime purchase has been processed, it usually cannot be cancelled, refunded or reversed. Please make sure the recipient's phone number, network and amount are correct before confirming the payment.",
      },
      {
        q: 'Is there a minimum or maximum amount I can send?',
        a: 'Minimum and maximum amounts vary by country and mobile network, and are shown in the app before you confirm your purchase.',
      },
    ],
  },
  {
    title: 'Payments',
    faqs: [
      {
        q: 'What payment methods does Smile Africa accept?',
        a: 'You can pay directly using Visa, Mastercard, or any other supported debit or credit card.',
      },
      {
        q: 'Is my card information safe?',
        a: 'Yes. Card payments are processed through encrypted, PCI-compliant payment providers. Smile Africa does not store your full card details.',
      },
      {
        q: 'Will I be charged any extra fees?',
        a: 'Any applicable fees are shown clearly before you confirm your purchase, so there are no surprises at checkout.',
      },
    ],
  },
  {
    title: 'Transactions & History',
    faqs: [
      {
        q: 'What is a transaction ID?',
        a: 'A transaction ID is a unique reference number linked to your payment. If you contact customer support, please provide the transaction ID so Smile Africa can review your transaction more quickly.',
      },
      {
        q: 'Why is my transaction pending?',
        a: 'A transaction may show as pending because of mobile network processing, payment review, fraud prevention checks, or third-party provider delays.',
      },
      {
        q: 'Why did my transaction fail?',
        a: 'A transaction may fail because of incorrect recipient details, an unsupported network, a declined card, or a technical error. If your transaction fails, check the app for the transaction status or contact customer support.',
      },
    ],
  },
  {
    title: 'Customer Support',
    faqs: [
      {
        q: 'How do I contact customer support?',
        a: "You can contact Smile Africa customer support through the app. Go to the main menu, select 'Help,' then 'Customer Support,' and choose 'Contact Customer Support.'",
      },
      {
        q: 'What information should I provide when contacting support?',
        a: "Please provide your transaction ID, the recipient's phone number, the mobile network, the amount, the date and time of the transaction, and a short description of the issue.",
      },
      {
        q: 'When will customer support respond?',
        a: 'Smile Africa aims to respond as quickly as possible. Response times may vary depending on the issue and whether mobile network partners need to be reviewed.',
      },
    ],
  },
];

const FAQ: React.FC = () => {
  const [open, setOpen] = useState<string | null>(null);

  const toggle = (key: string) => setOpen(prev => (prev === key ? null : key));

  return (
    <section id="faq" className="bg-black py-24">
      <div className="w-full px-[6vw]">

        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-xs font-bold tracking-widest text-gray-400 uppercase mb-4">
            Frequently Asked Questions
          </p>
          <h2
            className="font-bold text-white leading-tight"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
          >
            Answers to your pressing questions<br />about Smile Africa
          </h2>
        </div>

        <div className="flex flex-col items-start max-w-3xl mx-auto">

          {/* Accordion */}
          <div className="w-full space-y-10">
            {sections.map(section => (
              <div key={section.title}>
                {/* Subsection label */}
                <p className="text-xs font-bold tracking-widest uppercase mb-4" style={{ color: '#ff7233' }}>
                  {section.title}
                </p>

                <div className="space-y-3">
                  {section.faqs.map((faq, i) => {
                    const key = `${section.title}-${i}`;
                    const isOpen = open === key;
                    return (
                      <div
                        key={key}
                        className="rounded-2xl overflow-hidden"
                        style={{ background: isOpen ? '#ea3d05' : '#1F2937' }}
                      >
                        <button
                          className="w-full flex items-center justify-between px-6 py-5 text-left focus:outline-none"
                          onClick={() => toggle(key)}
                        >
                          <span className="text-white font-semibold text-base pr-4">
                            {faq.q}
                          </span>
                          <span className="flex-shrink-0 text-white text-xl font-light">
                            {isOpen ? '∧' : '+'}
                          </span>
                        </button>

                        {isOpen && (
                          <>
                            <div className="mx-6 border-t border-white/25" />
                            <p className="px-6 py-5 text-orange-50 text-sm leading-relaxed whitespace-pre-line">
                              {faq.a}
                            </p>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
};

export default FAQ;
